import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Play, Loader2, Zap, DollarSign, TrendingDown,
  Sparkles, Lock, ChevronRight, ArrowRight, BarChart3, Mail,
  Trophy, Clock, AlertCircle, CheckCircle2, Timer,
  Upload, Image, Eye, X, Mic, MicOff, FileAudio,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MODELS = [
  { id: "gpt-4o", label: "GPT-4o", provider: "OpenAI" },
  { id: "gpt-4o-mini", label: "GPT-4o Mini", provider: "OpenAI" },
  { id: "claude-sonnet-4-20250514", label: "Claude Sonnet 4", provider: "Anthropic" },
  { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash", provider: "Google" },
];

const TASK_TYPES = [
  { id: "general", label: "General" },
  { id: "code", label: "Code" },
  { id: "analysis", label: "Analysis" },
  { id: "creative", label: "Creative" },
  { id: "image", label: "Image", badge: "New" },
  { id: "vision", label: "Vision", badge: "New" },
  { id: "stt", label: "Speech-to-Text", badge: "New" },
];

const SAMPLE_PROMPTS = [
  "Explain quantum computing in simple terms",
  "Write a Python function to merge two sorted arrays",
  "Compare REST vs GraphQL for mobile apps",
  "Write a creative tagline for an AI startup",
];

const PIPELINE_STEPS = [
  "Analyzing prompt complexity...",
  "Selecting candidate models...",
  "Running parallel evaluation across providers...",
  "Scoring responses & selecting winner...",
];

export default function SandboxTryIt({ compact = false }) {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("gpt-4o");
  const [taskType, setTaskType] = useState("general");
  const [running, setRunning] = useState(false);
  const [pipelineStep, setPipelineStep] = useState(-1);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);
  const [emailGate, setEmailGate] = useState(false);
  const [email, setEmail] = useState("");
  const resultRef = useRef(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  // STT state
  const [sttRecording, setSttRecording] = useState(false);
  const [sttTranscribing, setSttTranscribing] = useState(false);
  const [sttTranscript, setSttTranscript] = useState(null);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioFileInputRef = useRef(null);
  // Inline voice-to-prompt state (mic in prompt field)
  const [inlineListening, setInlineListening] = useState(false);
  const [inlineTranscribing, setInlineTranscribing] = useState(false);
  const inlineRecorderRef = useRef(null);
  const inlineChunksRef = useRef([]);

  useEffect(() => {
    axios.get(`${API}/sandbox/status`).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  // Inline voice-to-prompt: tap mic → record → Whisper → fill prompt
  const toggleInlineVoice = useCallback(async () => {
    if (inlineListening && inlineRecorderRef.current) {
      inlineRecorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      inlineChunksRef.current = [];
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm",
      });
      mr.ondataavailable = (e) => { if (e.data.size > 0) inlineChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setInlineListening(false);
        const blob = new Blob(inlineChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Too short — speak a bit longer"); return; }
        setInlineTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "recording.webm");
          const res = await axios.post(`${API}/swarms/transcribe`, fd, { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true });
          const text = res.data?.text || res.data?.transcript || "";
          if (text) { setPrompt(prev => (prev ? prev + " " + text : text)); toast.success("Voice captured"); }
          else toast.error("No speech detected — try again");
        } catch (err) { toast.error("Transcription failed"); }
        setInlineTranscribing(false);
      };
      inlineRecorderRef.current = mr;
      mr.start(250);
      setInlineListening(true);
      toast.success("Listening... tap mic to stop");
    } catch (err) {
      if (err.name === "NotAllowedError") toast.error("Microphone access denied");
      else toast.error("Could not access microphone");
    }
  }, [inlineListening]);

  // File upload handlers for Vision mode
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file (PNG, JPG, WEBP)"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); return; }
    setUploadedImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const clearUpload = useCallback(() => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // Simulated image generation
  const runImageGeneration = async () => {
    if (!prompt.trim()) { toast.error("Enter an image prompt"); return; }
    setRunning(true);
    setPipelineStep(0);
    setGeneratedImage(null);
    const steps = ["Parsing image prompt...", "Selecting optimal image model...", "Generating image via DALL-E 3...", "Post-processing & optimization..."];
    for (let i = 0; i < steps.length; i++) {
      setPipelineStep(i);
      await new Promise(r => setTimeout(r, 800 + Math.random() * 600));
    }
    // Generate a placeholder SVG as mock output
    const colors = ["#06b6d4", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#3b82f6"];
    const c1 = colors[Math.floor(Math.random() * colors.length)];
    const c2 = colors[Math.floor(Math.random() * colors.length)];
    const svgStr = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="${c1}"/><stop offset="100%" stop-color="${c2}"/></linearGradient></defs><rect width="512" height="512" fill="url(#g)" rx="16"/><text x="256" y="240" text-anchor="middle" fill="white" font-family="sans-serif" font-size="20" opacity="0.8">Generated Image</text><text x="256" y="280" text-anchor="middle" fill="white" font-family="sans-serif" font-size="14" opacity="0.6">${prompt.slice(0, 40)}${prompt.length > 40 ? "..." : ""}</text></svg>`)}`;
    setGeneratedImage(svgStr);
    setResult({
      model_used: "dall-e-3",
      provider: "OpenAI",
      response_text: `Image generated for: "${prompt}"`,
      latency_ms: Math.round(1800 + Math.random() * 2000),
      cost_estimate: +(0.04 + Math.random() * 0.04).toFixed(4),
      tokens_used: 0,
    });
    setPipelineStep(-1);
    setRunning(false);
    toast.success("Image generated!");
  };

  // Simulated vision analysis
  const runVisionAnalysis = async () => {
    if (!uploadedImage) { toast.error("Upload an image first"); return; }
    if (!prompt.trim()) { toast.error("Describe what you want to analyze"); return; }
    setRunning(true);
    setPipelineStep(0);
    const steps = ["Encoding image...", "Selecting vision model...", "Running multi-modal analysis...", "Generating analysis report..."];
    for (let i = 0; i < steps.length; i++) {
      setPipelineStep(i);
      await new Promise(r => setTimeout(r, 700 + Math.random() * 500));
    }
    const analyses = [
      `**Image Analysis Results**\n\nThe uploaded image shows a complex visual composition. Based on the query "${prompt}":\n\n- **Primary Subject**: Detected with 94.2% confidence\n- **Scene Classification**: Indoor/Outdoor environment identified\n- **Color Palette**: Dominant colors extracted (${["warm tones", "cool blues", "earth tones", "vibrant mix"][Math.floor(Math.random() * 4)]})\n- **Text Detection**: ${Math.random() > 0.5 ? "Text elements found and extracted" : "No significant text detected"}\n- **Quality Score**: ${(85 + Math.random() * 14).toFixed(1)}/100\n\n*Analysis performed via QNNEAL multi-modal routing — optimal model selected based on image complexity.*`,
      `**Vision Analysis Complete**\n\nProcessed the uploaded image against your query: "${prompt}"\n\n- **Objects Detected**: ${Math.floor(3 + Math.random() * 8)} distinct objects identified\n- **Spatial Layout**: Composition analyzed with depth estimation\n- **Semantic Understanding**: Context and relationships mapped\n- **Anomalies**: ${Math.random() > 0.7 ? "Minor anomalies flagged for review" : "No anomalies detected"}\n- **Confidence**: ${(88 + Math.random() * 11).toFixed(1)}%\n\n*Routed through GPT-4o Vision for optimal quality-to-cost ratio.*`,
    ];
    setResult({
      model_used: "gpt-4o",
      provider: "OpenAI",
      response_text: analyses[Math.floor(Math.random() * analyses.length)],
      latency_ms: Math.round(1200 + Math.random() * 1500),
      cost_estimate: +(0.01 + Math.random() * 0.02).toFixed(4),
      tokens_used: Math.round(800 + Math.random() * 400),
    });
    setPipelineStep(-1);
    setRunning(false);
    toast.success("Vision analysis complete!");
  };

  // STT: Toggle microphone recording
  const toggleSTTRecording = async () => {
    if (sttRecording) {
      mediaRecorderRef.current?.stop();
      setSttRecording(false);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4" });
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType });
        if (blob.size < 100) { toast.error("Recording too short"); return; }
        await transcribeAudio(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setSttRecording(true);
      toast.info("Recording... speak now. Click again to stop.");
    } catch (err) {
      if (err.name === "NotAllowedError") toast.error("Microphone access denied");
      else toast.error("Could not access microphone");
    }
  };

  // STT: Handle audio file upload
  const handleAudioUpload = useCallback((e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please upload an audio file (WAV, MP3, WebM, etc.)"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Audio must be under 25MB"); return; }
    setUploadedAudio(file);
    setAudioPreview(URL.createObjectURL(file));
  }, []);

  // STT: Transcribe audio via Whisper endpoint
  const transcribeAudio = async (blobOrFile) => {
    setSttTranscribing(true);
    setSttTranscript(null);
    setResult(null);
    const startTime = Date.now();
    try {
      const formData = new FormData();
      formData.append("audio", blobOrFile, blobOrFile.name || "recording.webm");
      const res = await axios.post(`${API}/swarms/transcribe`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      const elapsed = Date.now() - startTime;
      const text = res.data?.text || res.data?.transcript || "";
      setSttTranscript(text);
      setResult({
        model_used: "whisper-1",
        provider: "OpenAI",
        response_text: text || "(No speech detected)",
        latency_ms: elapsed,
        cost_estimate: +(0.006 * ((blobOrFile.size || 0) / (1024 * 1024))).toFixed(4),
        tokens_used: text.split(/\s+/).length,
      });
      toast.success("Transcription complete!");
    } catch (err) {
      toast.error("Transcription failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSttTranscribing(false);
    }
  };

  const runSandbox = async (overrideEmail) => {
    if (!prompt.trim()) { toast.error("Enter a prompt first"); return; }
    setRunning(true);
    setResult(null);
    setEmailGate(false);

    for (let i = 0; i < PIPELINE_STEPS.length; i++) {
      setPipelineStep(i);
      await new Promise(r => setTimeout(r, [300, 400, 600, 300][i]));
    }

    try {
      const payload = { prompt: prompt.trim(), model, task_type: taskType };
      if (overrideEmail || email) payload.email = overrideEmail || email;
      const res = await axios.post(`${API}/sandbox/try`, payload);
      setResult(res.data);
      setStatus(s => s ? { ...s, remaining: res.data.remaining_tries, used: s.total - res.data.remaining_tries } : s);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail.error === "email_required") {
        setEmailGate(true);
      } else if (typeof detail === "object" && detail.error === "sandbox_limit_reached") {
        toast.error("No free tries remaining. Sign up to continue!");
        setStatus(s => s ? { ...s, remaining: 0 } : s);
      } else {
        toast.error(typeof detail === "string" ? detail : "Something went wrong");
      }
    }
    setRunning(false);
    setPipelineStep(-1);
  };

  const remaining = status?.remaining ?? 3;
  const exhausted = remaining <= 0 && status !== null;
  const opt = result?.optimization;
  const comp = result?.comparison;

  return (
    <div className={compact ? "" : "py-24"} id="try-it" data-testid="sandbox-section" data-automation-id="sandbox-section">
      <div className={compact ? "" : "max-w-4xl mx-auto px-6"}>
        {!compact && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-amber-500/15 text-amber-400 border-amber-500/20">Live Demo</Badge>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl tracking-tight mb-4">
              Try It Now — No Signup Required
            </h2>
            <p className="text-[var(--qn-foreground-muted)] max-w-2xl mx-auto text-base">
              Paste any prompt. QNNEAL evaluates multiple providers in parallel and picks the best value.
            </p>
          </motion.div>
        )}

        <Card className="bg-[var(--qn-card)] border-[var(--qn-border)] overflow-hidden" data-testid="sandbox-try-it" data-automation-id="sandbox-try-it">
          {compact && (
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                Try It Now
              </CardTitle>
              <p className="text-sm text-[var(--qn-foreground-muted)]">See how QNNEAL optimizes a real request. No API keys needed.</p>
            </CardHeader>
          )}
          <CardContent className={compact ? "space-y-4" : "p-6 space-y-5"}>
            {/* Task Type Pills */}
            <div className="flex flex-wrap gap-2" data-testid="task-type-selector" data-automation-id="task-type-selector">
              {TASK_TYPES.map(tt => (
                <button
                  key={tt.id}
                  onClick={() => {
                    setTaskType(tt.id);
                  }}
                  disabled={exhausted}
                  data-testid={`task-type-${tt.id}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-1.5 ${
                    taskType === tt.id
                      ? "bg-[var(--qn-primary)] text-white shadow-sm shadow-[var(--qn-primary)]/25"
                      : "bg-white/[0.06] text-[var(--qn-foreground-muted)] border border-white/[0.1] hover:bg-white/[0.1]"
                  }`}
                >
                  {tt.label}
                  {tt.badge && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tt.badge === "New" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{tt.badge}</span>}
                </button>
              ))}
            </div>

            {/* Model Selector + Remaining Badge */}
            <div className="flex items-center gap-3">
              <select
                value={model}
                onChange={e => setModel(e.target.value)}
                disabled={exhausted}
                className="bg-[var(--qn-background)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]"
                data-testid="model-selector" data-automation-id="model-selector"
              >
                {MODELS.map(m => (
                  <option key={m.id} value={m.id}>{`${m.label} (${m.provider})`}</option>
                ))}
              </select>
              {status !== null && (
                <Badge
                  variant="outline"
                  className={`text-xs ml-auto ${remaining > 0 ? "text-emerald-400 border-emerald-400/30" : "text-amber-400 border-amber-400/30"}`}
                  data-testid="sandbox-remaining-badge" data-automation-id="sandbox-remaining-badge"
                >
                  {remaining} free {remaining === 1 ? "try" : "tries"} remaining
                </Badge>
              )}
            </div>

            {/* Vision Upload Zone */}
            {taskType === "vision" && (
              <div data-testid="vision-upload-zone">
                {!imagePreview ? (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleFileDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200 ${
                      dragOver
                        ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/[0.06]"
                        : "border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40 bg-[var(--qn-surface)]/30"
                    }`}
                  >
                    <Upload className={`w-8 h-8 mx-auto mb-3 ${dragOver ? "text-[var(--qn-primary)]" : "text-[var(--qn-foreground-muted)]"}`} />
                    <p className="text-sm font-medium text-[var(--qn-foreground)]">
                      {dragOver ? "Drop image here" : "Drag & drop an image, or click to browse"}
                    </p>
                    <p className="text-xs text-[var(--qn-foreground-muted)] mt-1">PNG, JPG, WEBP up to 10MB</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleFileDrop}
                      className="hidden"
                      data-testid="vision-file-input"
                    />
                  </div>
                ) : (
                  <div className="relative rounded-xl overflow-hidden border border-[var(--qn-border)] bg-[var(--qn-surface)]/30">
                    <img src={imagePreview} alt="Uploaded" className="w-full max-h-[200px] object-contain" />
                    <button
                      onClick={clearUpload}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
                      data-testid="vision-clear-upload"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                    <div className="absolute bottom-2 left-2 px-2 py-1 rounded bg-black/60 text-[10px] text-white">
                      {uploadedImage.name} ({(uploadedImage.size / 1024).toFixed(0)}KB)
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Image Generation Preview */}
            {taskType === "image" && generatedImage && (
              <div className="rounded-xl overflow-hidden border border-[var(--qn-border)]" data-testid="image-generation-result">
                <img src={generatedImage} alt="Generated" className="w-full max-h-[280px] object-contain bg-[var(--qn-surface)]/30" />
              </div>
            )}

            {/* Speech-to-Text Input Zone */}
            {taskType === "stt" && (
              <div className="space-y-4" data-testid="stt-input-zone">
                <div className="flex flex-col items-center gap-4 p-6 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface)]/30">
                  {/* Record Button */}
                  <motion.button
                    onClick={toggleSTTRecording}
                    disabled={sttTranscribing}
                    whileTap={{ scale: 0.95 }}
                    className={`w-20 h-20 rounded-full flex items-center justify-center transition-all duration-200 ${
                      sttRecording
                        ? "bg-red-500 shadow-lg shadow-red-500/30 animate-pulse"
                        : sttTranscribing
                        ? "bg-amber-500 cursor-wait"
                        : "bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/80 shadow-lg shadow-[var(--qn-primary)]/20"
                    }`}
                    data-testid="stt-record-btn"
                  >
                    {sttTranscribing ? (
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    ) : sttRecording ? (
                      <MicOff className="w-8 h-8 text-white" />
                    ) : (
                      <Mic className="w-8 h-8 text-white" />
                    )}
                  </motion.button>
                  <p className="text-sm text-[var(--qn-foreground-muted)]">
                    {sttTranscribing ? "Transcribing with Whisper..." : sttRecording ? "Recording... click to stop" : "Click to record, or upload an audio file below"}
                  </p>

                  {/* Or divider */}
                  <div className="flex items-center gap-3 w-full">
                    <div className="h-px flex-1 bg-[var(--qn-border)]" />
                    <span className="text-xs text-[var(--qn-foreground-muted)]">or</span>
                    <div className="h-px flex-1 bg-[var(--qn-border)]" />
                  </div>

                  {/* Upload Audio File */}
                  <button
                    onClick={() => audioFileInputRef.current?.click()}
                    disabled={sttRecording || sttTranscribing}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40 bg-[var(--qn-surface)]/30 text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] transition-all"
                    data-testid="stt-upload-btn"
                  >
                    <FileAudio className="w-4 h-4" />
                    {uploadedAudio ? uploadedAudio.name : "Upload audio file (WAV, MP3, WebM up to 25MB)"}
                  </button>
                  <input
                    ref={audioFileInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleAudioUpload}
                    className="hidden"
                    data-testid="stt-file-input"
                  />

                  {/* Audio Preview */}
                  {audioPreview && (
                    <div className="w-full flex items-center gap-3 p-3 rounded-lg bg-[var(--qn-surface)]/50 border border-[var(--qn-border)]">
                      <audio controls src={audioPreview} className="flex-1 h-8" data-testid="stt-audio-preview" />
                      <button
                        onClick={() => { setUploadedAudio(null); setAudioPreview(null); if (audioFileInputRef.current) audioFileInputRef.current.value = ""; }}
                        className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center hover:bg-red-500/40 transition-colors"
                        data-testid="stt-clear-audio"
                      >
                        <X className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Transcription Result */}
                {sttTranscript && (
                  <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5" data-testid="stt-transcript-result">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm font-medium text-emerald-400">Transcription Result</span>
                    </div>
                    <p className="text-sm text-[var(--qn-foreground)] leading-relaxed">{sttTranscript}</p>
                  </div>
                )}
              </div>
            )}

            {/* Prompt Input — hidden for STT mode */}
            {taskType !== "stt" && (
            <div className="relative">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value.slice(0, 500))}
                placeholder={
                  inlineListening ? "Listening... speak your prompt"
                    : inlineTranscribing ? "Transcribing your voice..."
                    : taskType === "image"
                    ? "Describe the image you want to generate... or tap the mic"
                    : taskType === "vision"
                      ? "What would you like to analyze? Type or tap the mic"
                      : "Try a prompt... type or tap the mic to speak"
                }
                disabled={exhausted || inlineTranscribing}
                className="w-full min-h-[80px] p-4 pr-12 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)] placeholder:text-[var(--qn-foreground-subtle)] resize-none focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)] disabled:opacity-50"
                data-testid="sandbox-prompt-input" data-automation-id="sandbox-prompt-input"
              />
              <button
                onClick={toggleInlineVoice}
                disabled={exhausted || inlineTranscribing}
                className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                  inlineTranscribing
                    ? "bg-amber-500/20 cursor-wait"
                    : inlineListening
                    ? "bg-red-500 shadow-md shadow-red-500/30 animate-pulse"
                    : "bg-[var(--qn-surface)] hover:bg-[var(--qn-primary)]/20 border border-[var(--qn-border)] hover:border-[var(--qn-primary)]/40"
                }`}
                data-testid="sandbox-voice-btn"
                title={inlineListening ? "Stop recording" : "Tap to speak"}
              >
                {inlineTranscribing ? (
                  <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
                ) : inlineListening ? (
                  <MicOff className="w-4 h-4 text-white" />
                ) : (
                  <Mic className="w-4 h-4 text-[var(--qn-foreground-muted)]" />
                )}
              </button>
              {inlineListening && (
                <span className="absolute bottom-2 left-4 text-[10px] text-red-400 animate-pulse">Recording...</span>
              )}
              <span className="absolute bottom-2 right-3 text-xs text-[var(--qn-foreground-subtle)]">{prompt.length}/500</span>
            </div>
            )}

            {/* Sample Prompts — hidden for STT mode */}
            {!result && !exhausted && taskType !== "stt" && (
              <div className="flex flex-wrap gap-1.5" data-testid="sandbox-sample-prompts" data-automation-id="sandbox-sample-prompts">
                {SAMPLE_PROMPTS.map((sp, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(sp)}
                    className="text-xs px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-[var(--qn-foreground-muted)] hover:bg-white/[0.1] hover:text-[var(--qn-foreground)] transition-colors"
                    data-testid={`sample-prompt-${i}`}
                  >
                    {sp.length > 45 ? sp.slice(0, 45) + "..." : sp}
                  </button>
                ))}
              </div>
            )}

            {/* Email Gate */}
            <AnimatePresence>
              {emailGate && !running && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                  data-testid="sandbox-email-gate" data-automation-id="sandbox-email-gate"
                >
                  <div className="rounded-xl bg-[var(--qn-primary)]/[0.06] border border-[var(--qn-primary)]/20 p-5 text-center">
                    <Mail className="w-6 h-6 text-[var(--qn-primary)] mx-auto mb-2" />
                    <p className="text-sm font-medium text-[var(--qn-foreground)] mb-1">Enter your email to unlock your final free try</p>
                    <p className="text-xs text-[var(--qn-foreground-muted)] mb-4">We'll send you optimization tips — unsubscribe anytime.</p>
                    <form
                      onSubmit={e => { e.preventDefault(); if (email.trim()) runSandbox(email.trim()); }}
                      className="flex gap-2 max-w-sm mx-auto"
                    >
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        required
                        className="flex-1 bg-[var(--qn-background)] border border-[var(--qn-border)] rounded-lg px-3 py-2 text-sm text-[var(--qn-foreground)] placeholder:text-[var(--qn-foreground-subtle)] focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]"
                        data-testid="sandbox-email-input" data-automation-id="sandbox-email-input"
                      />
                      <Button type="submit" className="btn-primary px-4" disabled={!email.trim()} data-testid="sandbox-email-submit" data-automation-id="sandbox-email-submit">
                        Unlock <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </form>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Run Button or Exhausted State */}
            {!exhausted && !emailGate ? (
              taskType === "stt" ? (
                <Button
                  onClick={() => {
                    if (uploadedAudio) transcribeAudio(uploadedAudio);
                    else toast.info("Record audio or upload a file to transcribe");
                  }}
                  disabled={sttTranscribing || sttRecording || !uploadedAudio}
                  className="w-full btn-primary h-11 text-base"
                  data-testid="sandbox-stt-transcribe-btn"
                >
                  {sttTranscribing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transcribing with Whisper...</>
                  ) : (
                    <><FileAudio className="w-4 h-4 mr-2" />Transcribe Uploaded Audio</>
                  )}
                </Button>
              ) : (
              <Button
                onClick={() => {
                  if (taskType === "image") runImageGeneration();
                  else if (taskType === "vision") runVisionAnalysis();
                  else runSandbox();
                }}
                disabled={running || (!prompt.trim() && taskType !== "vision") || (taskType === "vision" && !uploadedImage)}
                className="w-full btn-primary h-11 text-base"
                data-testid="sandbox-run-btn"
                data-automation-id="sandbox-run-btn"
              >
                {running ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{taskType === "image" ? "Generating image..." : taskType === "vision" ? "Analyzing image..." : "Evaluating providers..."}</>
                ) : taskType === "image" ? (
                  <><Image className="w-4 h-4 mr-2" />Generate Image</>
                ) : taskType === "vision" ? (
                  <><Eye className="w-4 h-4 mr-2" />Analyze Image</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" />Try It Free</>
                )}
              </Button>
              )
            ) : (
              !emailGate && (
                <div className="rounded-xl bg-amber-500/8 border border-amber-500/15 p-6 text-center" data-testid="sandbox-exhausted" data-automation-id="sandbox-exhausted">
                  <Lock className="w-6 h-6 text-amber-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-amber-400 mb-1.5">You've experienced the power of QNNEAL optimization</p>
                  <p className="text-xs text-[var(--qn-foreground-muted)] mb-4">Sign up to unlock unlimited access — no credit card required.</p>
                  <Button
                    className="btn-primary px-6"
                    onClick={() => window.location.href = "/login"}
                    data-testid="sandbox-signup-cta" data-automation-id="sandbox-signup-cta"
                  >
                    Start Free <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )
            )}

            {/* Pipeline Animation */}
            <AnimatePresence>
              {running && pipelineStep >= 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] p-4 space-y-2" data-testid="pipeline-animation" data-automation-id="pipeline-animation">
                    {PIPELINE_STEPS.map((step, i) => (
                      <div key={i} className={`flex items-center gap-3 text-sm transition-all duration-300 ${i <= pipelineStep ? "text-[var(--qn-foreground)]" : "text-[var(--qn-foreground-subtle)]"}`}>
                        {i < pipelineStep ? (
                          <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center"><Zap className="w-3 h-3 text-emerald-400" /></div>
                        ) : i === pipelineStep ? (
                          <Loader2 className="w-5 h-5 text-[var(--qn-primary)] animate-spin" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-white/[0.05]" />
                        )}
                        {step}
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ==================== RESULTS ==================== */}
            <AnimatePresence>
              {result && opt && (
                <motion.div
                  ref={resultRef}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                  data-testid="sandbox-results" data-automation-id="sandbox-results"
                >
                  {/* Before / After Split */}
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-0 rounded-xl overflow-hidden border border-[var(--qn-border)]">
                    {/* Original (calculated, not called) */}
                    <div className="p-5 bg-red-500/[0.04] border-b md:border-b-0 md:border-r border-[var(--qn-border)]" data-testid="sandbox-without" data-automation-id="sandbox-without">
                      <div className="text-xs font-medium text-red-400/80 uppercase tracking-wider mb-3">Without QNNEAL</div>
                      <div className="space-y-2.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Model</span>
                          <span className="font-medium">{opt.original_model}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Provider</span>
                          <span>{opt.original_provider}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Cost (this prompt)</span>
                          <span className="font-mono">${formatCost(opt.original_cost)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Tokens</span>
                          <span className="font-mono text-[var(--qn-foreground-muted)]">{opt.input_tokens} in / {opt.output_tokens} out</span>
                        </div>
                      </div>
                    </div>

                    {/* Savings Arrow */}
                    <div className="hidden md:flex flex-col items-center justify-center px-4 bg-[var(--qn-background)]">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: "spring" }}
                        className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center"
                      >
                        <TrendingDown className="w-6 h-6 text-emerald-400" />
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="font-mono font-bold text-xl text-emerald-400 mt-2"
                        data-testid="sandbox-savings-pct" data-automation-id="sandbox-savings-pct"
                      >
                        {opt.cost_savings_percent > 0 ? `-${opt.cost_savings_percent}%` : "—"}
                      </motion.div>
                    </div>

                    {/* Winner */}
                    <div className="p-5 bg-emerald-500/[0.04]" data-testid="sandbox-with" data-automation-id="sandbox-with">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-medium text-emerald-400 uppercase tracking-wider">With QNNEAL</span>
                        <Trophy className="w-3.5 h-3.5 text-amber-400" />
                        <span className="text-[10px] text-amber-400 font-medium">WINNER</span>
                      </div>
                      <div className="space-y-2.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Model</span>
                          <span className="font-medium text-emerald-400">{opt.optimized_display || opt.optimized_model}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Provider</span>
                          <span>{opt.optimized_provider}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Cost (this prompt)</span>
                          <span className="font-mono text-emerald-400">${formatCost(opt.optimized_cost)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[var(--qn-foreground-muted)]">Latency</span>
                          <span className="font-mono">{opt.latency_ms}ms</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Metrics Bar */}
                  <div className="flex flex-wrap items-center justify-center gap-3 text-xs" data-testid="sandbox-metrics-bar">
                    {opt.cost_savings_percent > 0 && (
                      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20">
                        <DollarSign className="w-3 h-3 mr-1" />
                        {opt.cost_savings_percent}% savings
                      </Badge>
                    )}
                    <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/20">
                      <Zap className="w-3 h-3 mr-1" />
                      Quality: {opt.quality_score}
                    </Badge>
                    {comp && (
                      <Badge className="bg-violet-500/15 text-violet-400 border-violet-500/20">
                        <Timer className="w-3 h-3 mr-1" />
                        {comp.providers_evaluated} models in {(comp.parallel_execution_ms / 1000).toFixed(1)}s
                      </Badge>
                    )}
                    {result.cached && (
                      <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20">
                        Cached
                      </Badge>
                    )}
                  </div>

                  {/* Mobile savings badge */}
                  <div className="md:hidden flex justify-center">
                    <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-sm px-4 py-1">
                      {opt.cost_savings_percent > 0 ? `${opt.cost_savings_percent}% savings` : "Same cost"} &middot; {opt.latency_ms}ms
                    </Badge>
                  </div>

                  {/* Routing Reason */}
                  <div className="rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] p-4 text-sm text-[var(--qn-foreground-muted)]" data-testid="sandbox-routing-reason" data-automation-id="sandbox-routing-reason">
                    <BarChart3 className="w-4 h-4 text-[var(--qn-primary)] inline mr-2" />
                    {opt.routing_reason}
                  </div>

                  {/* Real Parallel Comparison Table */}
                  {comp?.cost_ranking?.length > 0 && (
                    <div className="rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] p-4" data-testid="sandbox-cost-chart" data-automation-id="sandbox-cost-chart">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs font-medium text-[var(--qn-foreground-muted)] uppercase tracking-wider">
                          All {comp.providers_evaluated} Models Evaluated (Parallel)
                        </div>
                        <div className="text-[10px] text-[var(--qn-foreground-subtle)]">
                          Savings est. based on 10,000 req/month
                        </div>
                      </div>

                      {/* Table header */}
                      <div className="hidden sm:grid grid-cols-[1fr_70px_65px_70px_65px_40px] gap-2 text-[10px] text-[var(--qn-foreground-subtle)] uppercase tracking-wider mb-2 px-1">
                        <span>Model</span>
                        <span className="text-right">Cost</span>
                        <span className="text-right">Quality</span>
                        <span className="text-right">Latency</span>
                        <span className="text-right">Tokens</span>
                        <span className="text-center">Status</span>
                      </div>

                      <div className="space-y-1">
                        {comp.cost_ranking.map((item, i) => {
                          const isWinner = item.is_winner;
                          const isFailed = item.status !== "success";
                          return (
                            <motion.div
                              key={item.model}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.06 }}
                              className={`grid grid-cols-1 sm:grid-cols-[1fr_70px_65px_70px_65px_40px] gap-1 sm:gap-2 items-center text-xs px-2 py-2 rounded-lg transition-colors ${
                                isWinner
                                  ? "bg-emerald-500/[0.08] border border-emerald-500/20"
                                  : isFailed
                                  ? "bg-red-500/[0.04] border border-red-500/10 opacity-60"
                                  : "bg-white/[0.02] hover:bg-white/[0.04]"
                              }`}
                              data-testid={`ranking-row-${item.model}`}
                            >
                              {/* Model name */}
                              <div className="flex items-center gap-2">
                                {isWinner && <Trophy className="w-3 h-3 text-amber-400 shrink-0" />}
                                <span className={`truncate font-medium ${isWinner ? "text-emerald-400" : "text-[var(--qn-foreground)]"}`}>
                                  {item.display || item.model}
                                </span>
                                <span className="text-[10px] text-[var(--qn-foreground-subtle)] hidden sm:inline">
                                  {item.provider}
                                </span>
                              </div>

                              {/* Cost */}
                              <span className={`sm:text-right font-mono ${isWinner ? "text-emerald-400" : "text-[var(--qn-foreground-muted)]"}`}>
                                <span className="sm:hidden text-[10px] text-[var(--qn-foreground-subtle)] mr-1">Cost:</span>
                                ${formatCost(item.cost)}
                              </span>

                              {/* Quality */}
                              <span className={`sm:text-right font-mono ${isWinner ? "text-emerald-400" : "text-[var(--qn-foreground-muted)]"}`}>
                                <span className="sm:hidden text-[10px] text-[var(--qn-foreground-subtle)] mr-1">Quality:</span>
                                {isFailed ? "—" : item.quality.toFixed(2)}
                              </span>

                              {/* Latency */}
                              <span className="sm:text-right font-mono text-[var(--qn-foreground-muted)]">
                                <span className="sm:hidden text-[10px] text-[var(--qn-foreground-subtle)] mr-1">Latency:</span>
                                {isFailed ? "—" : `${item.latency_ms}ms`}
                              </span>

                              {/* Tokens */}
                              <span className="sm:text-right font-mono text-[var(--qn-foreground-muted)]">
                                <span className="sm:hidden text-[10px] text-[var(--qn-foreground-subtle)] mr-1">Tokens:</span>
                                {isFailed ? "—" : `${item.input_tokens + item.output_tokens}`}
                              </span>

                              {/* Status */}
                              <div className="flex sm:justify-center">
                                {item.status === "success" ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                ) : item.status === "timeout" ? (
                                  <Clock className="w-3.5 h-3.5 text-amber-400" />
                                ) : (
                                  <AlertCircle className="w-3.5 h-3.5 text-red-400" />
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Quality Breakdown (winner) */}
                  {opt.quality_breakdown && Object.keys(opt.quality_breakdown).length > 0 && (
                    <div className="rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] p-4" data-testid="sandbox-quality-breakdown" data-automation-id="sandbox-quality-breakdown">
                      <div className="text-xs font-medium text-[var(--qn-foreground-muted)] uppercase tracking-wider mb-3">
                        Quality Score Breakdown — {opt.optimized_display || opt.optimized_model}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(opt.quality_breakdown).map(([key, val]) => (
                          <div key={key} className="flex items-center gap-1.5 text-xs bg-white/[0.04] rounded-md px-2.5 py-1.5 border border-white/[0.06]">
                            <span className="text-[var(--qn-foreground-muted)]">{formatBreakdownKey(key)}</span>
                            <span className="font-mono font-medium text-cyan-400">+{val.toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="flex items-center gap-1.5 text-xs bg-emerald-500/[0.08] rounded-md px-2.5 py-1.5 border border-emerald-500/20">
                          <span className="text-emerald-400 font-medium">Total</span>
                          <span className="font-mono font-bold text-emerald-400">{opt.quality_score}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* AI Response */}
                  <div className="rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] p-4" data-testid="sandbox-ai-response" data-automation-id="sandbox-ai-response">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-xs font-medium text-[var(--qn-foreground-muted)] uppercase tracking-wider">
                        AI Response via {opt.optimized_display || opt.optimized_model}
                      </div>
                      <Trophy className="w-3 h-3 text-amber-400" />
                    </div>
                    <p className="text-sm text-[var(--qn-foreground)] leading-relaxed whitespace-pre-wrap">{result.response}</p>
                  </div>

                  {/* Monthly Savings */}
                  {opt.estimated_monthly_savings && opt.estimated_monthly_savings !== "$0" && (
                    <div className="text-center text-xs text-[var(--qn-foreground-subtle)]" data-testid="sandbox-monthly-savings">
                      Estimated savings: <span className="text-emerald-400 font-medium">{opt.estimated_monthly_savings}/month</span> at 10,000 requests/month
                    </div>
                  )}

                  {/* CTA */}
                  <div className="text-center pt-2">
                    {remaining > 0 ? (
                      <p className="text-sm text-[var(--qn-foreground-muted)]">
                        {result.cta?.message || `${remaining} free tries remaining.`}
                      </p>
                    ) : (
                      <Button className="btn-primary px-8" onClick={() => window.location.href = "/login"} data-testid="sandbox-result-signup" data-automation-id="sandbox-result-signup">
                        Start Free — No Credit Card Required <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function formatCost(cost) {
  if (!cost || cost === 0) return "0.0000";
  if (cost < 0.0001) return cost.toFixed(6);
  if (cost < 0.01) return cost.toFixed(4);
  return cost.toFixed(4);
}

function formatBreakdownKey(key) {
  const labels = {
    substance: "Substance",
    coherence: "Coherence",
    relevance: "Relevance",
    task_code: "Code Quality",
    task_analysis: "Analysis Depth",
    task_creative: "Creativity",
    task_general: "Clarity",
  };
  return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
